// src/components/chat/VoiceToTextButton.tsx
// -----------------------------------------------------------------------------
// Self-contained mic button: records, finalizes in transcribe-only mode (NO
// patient Document, NO extraction), and returns the transcript via onTranscript.
// Used by the patient-create Description field (cursor_logs/035 B4) so the doctor
// can dictate, then edit, then submit (which creates the record from the edited
// text). The in-chat mic with its own two-phase send lives in ChatInterface.
// -----------------------------------------------------------------------------

import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
  useTheme,
} from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import { fetchData, uploadData } from "api/apiHelpers";
import { useVoiceRecorder } from "hooks/useVoiceRecorder";
import { useNotification } from "hooks/useNotification";
import logger from "utils/logger";
import { useTranslation, useLanguageSwitcher } from "../../i18n";
import { speechLocalesFor, warnIfNonEnglish } from "../../i18n/speechLocale";
import RecordingControls from "./RecordingControls";

interface VoiceToTextButtonProps {
  onTranscript: (text: string) => void;
  /** Optional patient association (for billing usage). Omit in the create form. */
  patientId?: string;
  disabled?: boolean;
  /** Fired with true while recording/transcribing so the host can lock the field. */
  onActiveChange?: (active: boolean) => void;
}

interface FinalizePost {
  task_id: string;
  chunk_warning?: string | null;
}

interface FinalizeStatus {
  status: "pending" | "processing" | "completed" | "error";
  transcript?: string;
  formatted?: { formatted_text: string };
  language?: string | null;
  error?: string;
}

const VoiceToTextButton: React.FC<VoiceToTextButtonProps> = ({
  onTranscript,
  patientId,
  disabled,
  onActiveChange,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { language } = useLanguageSwitcher();
  const notification = useNotification();
  const [processing, setProcessing] = useState(false);
  // Read inside the (async) recorder callback so we use the language active when
  // the recording finishes, not a stale closure value.
  const langRef = useRef(language);
  langRef.current = language;

  const {
    recording,
    amplitude,
    recordingDuration,
    startRecording,
    stopRecording,
    cancelRecording,
  } = useVoiceRecorder(
    async (_blob, _url, name) => {
      const match = name.match(/voice_([a-f0-9-]+)_/);
      if (!match) {
        logger.error("[VoiceToText] could not extract session id from", name);
        return;
      }
      const sessionId = match[1];
      const chunksMatch = name.match(/_(\d+)chunks/);
      const expectedChunks = chunksMatch
        ? parseInt(chunksMatch[1], 10)
        : undefined;

      setProcessing(true);
      try {
        // Transcribe-only: no patient Document, no extraction (backend defaults
        // create_document=false). The transcript fills the textarea; the doctor
        // edits and submitting the form creates the record.
        const resp = (await uploadData(
          "/recording/finalize/",
          {
            session_id: sessionId,
            patient_id: patientId,
            expected_chunks: expectedChunks,
            locales: speechLocalesFor(langRef.current),
          },
          "application/json"
        )) as FinalizePost;

        if (resp.chunk_warning) notification.warning(resp.chunk_warning);

        const taskId = resp.task_id;
        for (let attempt = 0; attempt < 60; attempt++) {
          await new Promise((r) => setTimeout(r, 3000));
          const s = (await fetchData(
            `/recording/finalize/${taskId}/`
          )) as FinalizeStatus;
          if (s.status === "completed") {
            const txt = s.formatted?.formatted_text || s.transcript || "";
            if (txt) {
              onTranscript(txt);
              warnIfNonEnglish(s.language, notification, t);
            } else {
              notification.error(
                t(
                  "chat.transcription_failed_no_text",
                  "Transcription failed — no text received"
                )
              );
            }
            setProcessing(false);
            return;
          }
          if (s.status === "error") {
            throw new Error(s.error || "Transcription failed");
          }
        }
        throw new Error("Transcription timeout");
      } catch (e) {
        logger.error("[VoiceToText] finalize failed", e);
        notification.error(
          t("chat.transcription_failed", "Transcription failed")
        );
        setProcessing(false);
      }
    },
    patientId
  );

  // Lock the host field while recording or transcribing.
  useEffect(() => {
    onActiveChange?.(recording || processing);
  }, [recording, processing, onActiveChange]);

  // Transcribing — spinner.
  if (processing) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", px: 0.5 }}>
        <CircularProgress size={18} thickness={5} />
      </Box>
    );
  }

  // Recording — shared ChatGPT-style controls (amplitude + ✕ discard / ✓ accept).
  if (recording) {
    return (
      <RecordingControls
        amplitude={amplitude}
        duration={recordingDuration}
        onDiscard={cancelRecording}
        onStop={stopRecording}
      />
    );
  }

  // Idle — mic button.
  return (
    <Tooltip title={t("chat.record_voice", "Record voice note")}>
      <span>
        <IconButton
          onClick={startRecording}
          disabled={disabled}
          size="small"
          aria-label={t("chat.record_voice", "Record voice note")}
          sx={{
            color: theme.palette.text.secondary,
            "&:hover": { backgroundColor: theme.palette.action.hover },
          }}
        >
          <MicIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default VoiceToTextButton;
